export const DEFAULT_LANGUAGE = 'en';
export const SUPPORTED_LANGUAGES = ['en', 'tr', 'ru'];
export const PREFIXED_LANGUAGES = ['tr', 'ru'];
export const ACTIVE_LANGUAGE_STORAGE_KEY = 'tarkovActiveLanguage';

const normalizePathname = (pathname = '/') => {
  if (!pathname) return '/';
  const [pathOnly] = String(pathname).split(/[?#]/);
  if (!pathOnly.startsWith('/')) return `/${pathOnly}`;
  return pathOnly.replace(/\/+$/, '') || '/';
};

export const isSupportedLanguage = (language) => SUPPORTED_LANGUAGES.includes(language);

export const getLanguageFromPath = (pathname = '') => {
  const [, firstSegment] = normalizePathname(pathname).split('/');
  return SUPPORTED_LANGUAGES.includes(firstSegment) ? firstSegment : null;
};

export const stripLocalePrefix = (pathname = '/') => {
  const normalized = normalizePathname(pathname);
  const segments = normalized.split('/');
  if (SUPPORTED_LANGUAGES.includes(segments[1])) {
    const stripped = `/${segments.slice(2).join('/')}`;
    return stripped === '/' ? '/' : stripped.replace(/\/+$/, '');
  }
  return normalized;
};

export const withLocalePrefix = (pathname = '/', language = DEFAULT_LANGUAGE) => {
  const supportedLanguage = isSupportedLanguage(language) ? language : DEFAULT_LANGUAGE;
  const normalized = normalizePathname(pathname);
  if (supportedLanguage === DEFAULT_LANGUAGE) return normalized;
  return `/${supportedLanguage}${normalized === '/' ? '' : normalized}`;
};

export const getActiveLanguage = () => {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(ACTIVE_LANGUAGE_STORAGE_KEY);
};

export const setActiveLanguage = (language) => {
  if (typeof window === 'undefined' || !isSupportedLanguage(language)) return;
  sessionStorage.setItem(ACTIVE_LANGUAGE_STORAGE_KEY, language);
};

export const ROUTE_PATHS = {
  dashboard: '/dashboard',
  quests: '/quests',
  'kappa-items': '/kappa-items',
  hideout: '/hideout',
  story: '/story',
  'kappa-path': '/kappa-path',
  'privacy-policy': '/privacy-policy',
  'terms-of-service': '/terms-of-service',
  about: '/about',
  contact: '/contact',
};

const LEGACY_ROUTE_ALIASES = {
  '/panel': 'dashboard',
  '/gorev-rehberi': 'quests',
  '/kappa-esyalari': 'kappa-items',
  '/hideout-rehberi': 'hideout',
  '/kappa-rehberi': 'kappa-path',
  '/hikaye-rehberi': 'story',
};

export const localizedPath = (routeId, language = DEFAULT_LANGUAGE) => {
  const canonicalPath = ROUTE_PATHS[routeId] || ROUTE_PATHS.dashboard;

  if (routeId === 'dashboard' && language === DEFAULT_LANGUAGE) {
    return '/';
  }

  return withLocalePrefix(canonicalPath, language);
};

export const localizedQuestPath = (questSlug, language = DEFAULT_LANGUAGE) => {
  return withLocalePrefix(`/quest/${questSlug}`, language);
};

export const localizedStoryPath = (chapterId, language = DEFAULT_LANGUAGE) => {
  return withLocalePrefix(`/story/${chapterId}`, language);
};

export const routeIdFromPath = (pathname = '') => {
  const corePath = stripLocalePrefix(pathname);

  if (corePath === '/' || corePath === '/dashboard' || corePath === '/panel') return 'dashboard';
  if (corePath === '/quests' || corePath === '/gorev-rehberi' || corePath.startsWith('/quest/') || corePath.startsWith('/gorev/')) return 'quests';
  if (corePath === '/kappa-items' || corePath === '/kappa-esyalari') return 'kappa-items';
  if (corePath === '/hideout' || corePath === '/hideout-rehberi') return 'hideout';
  if (corePath === '/story' || corePath === '/hikaye-rehberi' || corePath.startsWith('/story/') || corePath.startsWith('/guide/') || corePath.startsWith('/rehber/')) return 'story';
  if (corePath === '/kappa-path' || corePath === '/kappa-rehberi') return 'kappa-path';
  if (corePath === '/privacy-policy') return 'privacy-policy';
  if (corePath === '/terms-of-service') return 'terms-of-service';
  if (corePath === '/about') return 'about';
  if (corePath === '/contact') return 'contact';

  return 'dashboard';
};

export const getCanonicalPath = (pathname = '/') => {
  const normalized = normalizePathname(pathname);
  const routeLanguage = getLanguageFromPath(normalized) || DEFAULT_LANGUAGE;
  const corePath = stripLocalePrefix(normalized);

  const questMatch = corePath.match(/^\/(?:quest|gorev)\/([^/]+)$/);
  if (questMatch) return localizedQuestPath(questMatch[1], routeLanguage);

  const storyMatch = corePath.match(/^\/(?:story|guide|rehber)\/([^/]+)$/);
  if (storyMatch) return localizedStoryPath(storyMatch[1], routeLanguage);

  const legacyRouteId = LEGACY_ROUTE_ALIASES[corePath];
  if (legacyRouteId) return localizedPath(legacyRouteId, routeLanguage);

  if (corePath === '/' && routeLanguage !== DEFAULT_LANGUAGE) {
    return localizedPath('quests', routeLanguage);
  }

  return localizedPath(routeIdFromPath(normalized), routeLanguage);
};

export const localizeCurrentPath = (pathname = '/', language = DEFAULT_LANGUAGE) => {
  const supportedLanguage = isSupportedLanguage(language) ? language : DEFAULT_LANGUAGE;
  const canonicalPath = getCanonicalPath(pathname);
  const corePath = stripLocalePrefix(canonicalPath);

  const questMatch = corePath.match(/^\/quest\/([^/]+)$/);
  if (questMatch) return localizedQuestPath(questMatch[1], supportedLanguage);

  const storyMatch = corePath.match(/^\/story\/([^/]+)$/);
  if (storyMatch) return localizedStoryPath(storyMatch[1], supportedLanguage);

  return localizedPath(routeIdFromPath(canonicalPath), supportedLanguage);
};
